import { useMemo } from 'react'
import { FileKey, KeyRound, ShieldCheck } from 'lucide-react'
import type { AdminColumn } from '@/components/DataTable/types'
import { RegistryCrudTable, type RegField, type RegRow } from '@/components/RegistryCrudTable'
import { DEVX_STR as S } from './devApiExt'

/**
 * A13.13/14/15 — kalit va sertifikat infra reyestrlari. Endi JONLI: admin
 * KMS kalit metama'lumotini (A13.13), four-eyes+HSM kalit-eksport so'rovlarini
 * (A13.14) va TLS sertifikatlar muddatini (A13.15) o'zi boshqaradi — real CRUD
 * (`/admin/developers/{kms-keys,key-exports,tls-certs}`, migration 000249). Tashqi
 * provayder ulanmasa ham foydali: admin reyestrni qo'lda yuritadi (soxta ma'lumot yo'q).
 */

interface KmsKeyRow extends RegRow {
  id: number
  alias: string
  provider: string
  status: string
  rotated_at: string
  created_at: string
}

interface KeyExportRow extends RegRow {
  id: number
  key_id: string
  approver: string
  status: string
  reason: string
  created_at: string
}

interface TLSCertRow extends RegRow {
  id: number
  domain: string
  issuer: string
  expires_at: string
  status: string
  created_at: string
}

/** A13.13 — Shifrlash kalitlari (KMS). */
export function KmsKeysScreen() {
  const columns = useMemo<AdminColumn<KmsKeyRow>[]>(
    () => [
      { key: 'alias', header: S.alias, type: 'text', minWidth: 180 },
      { key: 'provider', header: S.provider, type: 'text', width: 160 },
      {
        key: 'status',
        header: S.state,
        type: 'enum',
        width: 160,
        filterable: true,
        options: [
          { value: 'enabled', label: 'enabled', status: 'active' },
          { value: 'disabled', label: 'disabled', status: 'deleted' },
          { value: 'pending_deletion', label: 'pending_deletion', status: 'pending' },
        ],
      },
      { key: 'rotated_at', header: S.rotated, type: 'text', width: 150 },
      { key: 'created_at', header: S.createdCol, type: 'text', width: 150 },
    ],
    [],
  )
  const fields = useMemo<RegField[]>(
    () => [
      { name: 'alias', placeholder: S.alias },
      { name: 'provider', placeholder: S.provider },
      {
        name: 'status',
        placeholder: S.state,
        type: 'select',
        options: [
          { value: 'enabled', label: 'enabled' },
          { value: 'disabled', label: 'disabled' },
          { value: 'pending_deletion', label: 'pending_deletion' },
        ],
      },
      { name: 'rotated_at', placeholder: S.rotated, type: 'date' },
    ],
    [],
  )
  return (
    <RegistryCrudTable<KmsKeyRow>
      screenId="A13.13"
      title={S.kms}
      description={S.kmsDesc}
      addLabel={S.addKms}
      path="/admin/developers/kms-keys"
      listKey="keys"
      searchKeys={['alias', 'provider', 'status']}
      fields={fields}
      columns={columns}
      icon={<KeyRound size={28} strokeWidth={1.5} />}
    />
  )
}

/** A13.14 — Kalit eksporti (four-eyes + HSM). */
export function KeyExportScreen() {
  const columns = useMemo<AdminColumn<KeyExportRow>[]>(
    () => [
      { key: 'key_id', header: S.keyRef, type: 'text', minWidth: 160 },
      { key: 'approver', header: S.approver, type: 'text', width: 160 },
      {
        key: 'status',
        header: S.status,
        type: 'enum',
        width: 150,
        filterable: true,
        options: [
          { value: 'pending', label: 'pending', status: 'pending' },
          { value: 'approved', label: 'approved', status: 'active' },
          { value: 'rejected', label: 'rejected', status: 'deleted' },
          { value: 'exported', label: 'exported', status: 'review' },
        ],
      },
      { key: 'reason', header: S.reason, type: 'text', minWidth: 180 },
      { key: 'created_at', header: S.requestedAt, type: 'text', width: 150 },
    ],
    [],
  )
  const fields = useMemo<RegField[]>(
    () => [
      { name: 'key_id', placeholder: S.keyRef, mono: true },
      { name: 'approver', placeholder: S.approver },
      {
        name: 'status',
        placeholder: S.status,
        type: 'select',
        options: [
          { value: 'pending', label: 'pending' },
          { value: 'approved', label: 'approved' },
          { value: 'rejected', label: 'rejected' },
          { value: 'exported', label: 'exported' },
        ],
      },
      { name: 'reason', placeholder: S.reason },
    ],
    [],
  )
  return (
    <RegistryCrudTable<KeyExportRow>
      screenId="A13.14"
      title={S.keyExport}
      description={S.keyExportDesc}
      addLabel={S.addExport}
      path="/admin/developers/key-exports"
      listKey="requests"
      searchKeys={['key_id', 'approver', 'status']}
      fields={fields}
      columns={columns}
      icon={<FileKey size={28} strokeWidth={1.5} />}
    />
  )
}

/** A13.15 — Sertifikatlar (TLS) monitoringi. */
export function CertsScreen() {
  const columns = useMemo<AdminColumn<TLSCertRow>[]>(
    () => [
      { key: 'domain', header: S.domain, type: 'text', minWidth: 180 },
      { key: 'issuer', header: S.issuer, type: 'text', width: 180 },
      { key: 'expires_at', header: S.expiresAt, type: 'text', width: 150 },
      {
        key: 'status',
        header: S.status,
        type: 'enum',
        width: 140,
        filterable: true,
        options: [
          { value: 'valid', label: 'valid', status: 'active' },
          { value: 'expiring', label: 'expiring', status: 'pending' },
          { value: 'expired', label: 'expired', status: 'deleted' },
          { value: 'revoked', label: 'revoked', status: 'review' },
        ],
      },
    ],
    [],
  )
  const fields = useMemo<RegField[]>(
    () => [
      { name: 'domain', placeholder: S.domain, mono: true },
      { name: 'issuer', placeholder: S.issuer },
      { name: 'expires_at', placeholder: S.expiresAt, type: 'date' },
      {
        name: 'status',
        placeholder: S.status,
        type: 'select',
        options: [
          { value: 'valid', label: 'valid' },
          { value: 'expiring', label: 'expiring' },
          { value: 'expired', label: 'expired' },
          { value: 'revoked', label: 'revoked' },
        ],
      },
    ],
    [],
  )
  return (
    <RegistryCrudTable<TLSCertRow>
      screenId="A13.15"
      title={S.certs}
      description={S.certsDesc}
      addLabel={S.addCert}
      path="/admin/developers/tls-certs"
      listKey="certs"
      searchKeys={['domain', 'issuer', 'status']}
      fields={fields}
      columns={columns}
      icon={<ShieldCheck size={28} strokeWidth={1.5} />}
    />
  )
}
